import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, XCircle, RefreshCw, CheckCircle2, ReceiptText, Hand, Truck, PackageCheck, Clock, Mail, Phone, MapPin, MessageSquare, Store, ArrowLeft, RotateCcw } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { OrganizerContactBlock } from "../components/OrganizerContactBlock";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatDate, formatDateTime } from "../lib/api";
import { addressLine, deliveryLabel, fulfillmentMeta, nok, orderStatusMeta, paymentLabel, productUrl, shopUrl } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 min

// Ordreside/kvittering for kjøpar: /butikk/ordre/:orderId – Vipps-polling medan ordren ventar, fakturainfo + PDF, leveringsstatus.
export default function ShopOrderPage() {
  const { orderId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollCount = useRef(0);
  const stopped = useRef(false);

  const loadOrder = () => api.get(`/shop/orders/${orderId}`).then((res) => setData(res.data));

  useEffect(() => {
    loadOrder().catch((err) => {
      if (err?.response?.status === 404) setNotFound(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const pending = data?.order && ["pending_payment", "processing"].includes(data.order.status);

  useEffect(() => {
    if (!pending) return undefined;
    stopped.current = false;
    pollCount.current = 0;
    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data: st } = await api.get(`/shop/orders/${orderId}/payment-status`);
        if (stopped.current) return;
        if (st.status !== "pending") {
          stopped.current = true;
          await loadOrder();
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setNotFound(true);
          stopped.current = true;
          return;
        }
      }
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setTimedOut(true);
        stopped.current = true;
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      stopped.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, orderId]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-lg mx-auto px-4 py-24 text-center space-y-4" data-testid="shop-order-not-found">
          <XCircle className="w-10 h-10 mx-auto text-destructive" />
          <h1 className="text-xl font-heading font-semibold">{t("Fant ikke bestillingen")}</h1>
          <p className="text-sm text-muted-foreground">{t("Sjekk at lenken er riktig.")}</p>
          <Button asChild className="mt-2" data-testid="shop-order-home-button"><Link to="/butikk"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til alle butikker")}</Link></Button>
        </div>
      </PublicLayout>
    );
  }

  if (!data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-10 space-y-4" data-testid="shop-order-loading"><Skeleton className="h-10 w-1/2" /><Skeleton className="h-40 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>
      </PublicLayout>
    );
  }

  const { order, email_enabled: emailEnabled } = data;
  const st = orderStatusMeta(order);
  const fu = fulfillmentMeta(order);
  const committed = ["paid", "invoice_sent"].includes(order.status);
  const invoicePdf = order.invoice_url ? `${process.env.REACT_APP_BACKEND_URL}${order.invoice_url}` : "";
  const firstItem = order.items?.[0];

  const statusBlock = () => {
    if (pending && timedOut) {
      return (
        <div className="text-center space-y-3" data-testid="shop-order-timeout">
          <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground" />
          <h2 className="text-lg font-heading font-semibold">{t("Vi venter fortsatt på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Vi har ikke fått bekreftelse enda. Hvis du har betalt, prøv å oppdatere siden.")}</p>
          <Button onClick={() => window.location.reload()} data-testid="shop-order-retry-button"><RefreshCw className="w-4 h-4 mr-1.5" />{t("Sjekk på nytt")}</Button>
        </div>
      );
    }
    if (pending) {
      return (
        <div className="text-center space-y-3" data-testid="shop-order-pending">
          <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
          <h2 className="text-lg font-heading font-semibold">{t("Venter på Vipps")}</h2>
          <p className="text-sm text-muted-foreground">{t("Vi venter på bekreftelse fra Vipps. Fullfør betalingen i Vipps-appen – siden oppdateres automatisk.")}</p>
        </div>
      );
    }
    if (order.status === "invoice_sent") {
      return (
        <div className="space-y-3" data-testid="shop-order-invoice">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand))]"><ReceiptText className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Faktura sendt")}</h2></div>
          <p className="text-sm leading-relaxed" data-testid="shop-order-invoice-text">
            {t("Fakturaen er sendt til {email}. Betal {amount} til kontonummer {account} innen {due}, merket {invoice}.", { email: order.email, amount: nok(order.total_amount), account: order.invoice_account || "—", due: formatDate(order.invoice_due_at), invoice: order.invoice_number })}
          </p>
          <div className="grid gap-2 sm:grid-cols-3 text-sm">
            <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">{t("Kontonummer")}</p><p className="font-mono font-medium" data-testid="shop-order-invoice-account">{order.invoice_account || "—"}</p></div>
            <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">{t("Beløp")}</p><p className="font-medium tabular-nums">{nok(order.total_amount)}</p></div>
            <div className="rounded-lg bg-muted/50 p-3"><p className="text-xs text-muted-foreground">{t("Forfall")}</p><p className="font-medium">{formatDate(order.invoice_due_at)}</p></div>
          </div>
          {invoicePdf && (
            <Button asChild variant="secondary" data-testid="order-status-download-invoice-button"><a href={invoicePdf} target="_blank" rel="noreferrer"><ReceiptText className="w-4 h-4 mr-1.5" /> {t("Last ned faktura (PDF)")}</a></Button>
          )}
        </div>
      );
    }
    if (order.status === "paid") {
      return (
        <div className="space-y-3" data-testid="shop-order-paid">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]"><CheckCircle2 className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Takk! Betalingen er registrert.")}</h2></div>
          <p className="text-sm text-muted-foreground">{order.paid_at ? t("Betalt {date}", { date: formatDateTime(order.paid_at) }) : null}{emailEnabled ? ` · ${t("Du får ordrebekreftelse på e-post.")}` : ""}</p>
          {invoicePdf && <Button asChild variant="secondary" size="sm" data-testid="order-status-download-invoice-button"><a href={invoicePdf} target="_blank" rel="noreferrer"><ReceiptText className="w-4 h-4 mr-1.5" /> {t("Last ned faktura (PDF)")}</a></Button>}
        </div>
      );
    }
    if (order.status === "cancelled") {
      return (
        <div className="space-y-3" data-testid="shop-order-cancelled">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-[hsl(var(--danger))]/10 text-[hsl(var(--danger))]"><XCircle className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Kansellert")}</h2></div>
          <p className="text-sm text-muted-foreground">{order.payment_method === "vipps" && !order.paid_at ? t("Betalingen er avbrutt. Du kan prøve igjen fra varen.") : t("Bestillingen er kansellert. Er det betalt noe, blir det tilbakebetalt.")}</p>
          {firstItem && <Button asChild variant="outline" size="sm" data-testid="shop-order-retry-product-button"><Link to={productUrl(order.organizer_id, firstItem.product_id)}><RotateCcw className="w-4 h-4 mr-1.5" /> {t("Fortsett å handle")}</Link></Button>}
        </div>
      );
    }
    if (order.status === "refunded" || order.status === "refunding") {
      return (
        <div className="space-y-2" data-testid="shop-order-refunded">
          <div className="flex items-center gap-3"><span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-secondary text-muted-foreground"><RotateCcw className="w-5 h-5" /></span><h2 className="text-lg font-heading font-semibold">{t("Refundert")}</h2></div>
          <p className="text-sm text-muted-foreground">{t("Bestillingen er refundert ({amount}).", { amount: nok(order.refund_amount ?? order.total_amount) })}{order.refunded_at ? ` · ${formatDateTime(order.refunded_at)}` : ""}</p>
        </div>
      );
    }
    return null;
  };

  const sellerReason = order.cancel_reason || order.refund_reason;
  const steps = order.delivery_method === "shipping"
    ? [{ key: "pending", label: t("Mottatt"), Icon: Clock }, { key: "shipped", label: t("Sendt"), Icon: Truck }]
    : [{ key: "pending", label: t("Mottatt"), Icon: Clock }, { key: "ready", label: t("Klar for henting"), Icon: PackageCheck }, { key: "picked_up", label: t("Hentet"), Icon: Hand }];
  const stepIndex = Math.max(0, steps.findIndex((s) => s.key === order.fulfillment));

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6" data-testid="shop-order-page">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{t("Bestillingen din")}</p>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="shop-order-number">{t("Ordre {number}", { number: order.order_number })}</h1>
            <p className="text-sm text-muted-foreground mt-1">{formatDateTime(order.created_at)} · {paymentLabel(order)} · {deliveryLabel(order)}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge className={st.className} data-testid="order-status-badge">{st.label}</Badge>
            {committed && <Badge className={fu.className} data-testid="order-fulfillment-badge">{fu.label}</Badge>}
          </div>
        </div>

        <Card className="rounded-2xl border-border"><CardContent className="p-6">{statusBlock()}</CardContent></Card>

        {sellerReason && (
          <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm flex gap-3" data-testid="shop-order-seller-reason">
            <MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
            <p><span className="block text-xs text-muted-foreground">{t("Melding fra selger")}</span>{sellerReason}</p>
          </div>
        )}

        {committed && (
          <Card className="rounded-2xl border-border" data-testid="shop-order-fulfillment">
            <CardContent className="p-6 space-y-4">
              <h2 className="font-heading font-semibold">{t("Levering")}</h2>
              <ol className="flex items-center gap-2" aria-label={t("Levering")}>
                {steps.map((s, i) => {
                  const done = i <= stepIndex;
                  return (
                    <li key={s.key} className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${done ? "bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]" : "bg-secondary text-muted-foreground"}`} data-testid={`shop-order-step-${s.key}`} data-done={done ? "true" : "false"}><s.Icon className="w-4 h-4" /></span>
                      <span className={`text-xs sm:text-sm truncate ${done ? "font-medium" : "text-muted-foreground"}`}>{s.label}</span>
                      {i < steps.length - 1 && <span className={`h-px flex-1 ${i < stepIndex ? "bg-[hsl(var(--brand))]" : "bg-border"}`} aria-hidden="true" />}
                    </li>
                  );
                })}
              </ol>
              <div className="text-sm flex items-start gap-2">
                {order.delivery_method === "shipping" ? <Truck className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /> : <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />}
                <p>
                  <span className="block text-xs text-muted-foreground">{order.delivery_method === "shipping" ? t("Sendes til") : t("Hentested")}</span>
                  <span data-testid="shop-order-delivery-detail">{order.delivery_method === "shipping" ? addressLine(order.address) : order.pickup_place || t("Hentes etter avtale hos arrangøren")}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="rounded-2xl border-border" data-testid="order-status-summary">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading font-semibold">{t("Varer")}</h2>
            <div className="rounded-lg border border-border divide-y divide-border text-sm">
              {(order.items || []).map((i, idx) => (
                <div key={`${i.product_id}-${idx}`} className="flex justify-between gap-3 p-3" data-testid={`shop-order-item-${idx}`}>
                  <Link to={productUrl(order.organizer_id, i.product_id)} className="hover:underline underline-offset-4 min-w-0 truncate">{i.name} × {i.quantity}</Link>
                  <span className="tabular-nums shrink-0">{nok(i.line_total)}</span>
                </div>
              ))}
              {order.delivery_method === "shipping" && <div className="flex justify-between gap-3 p-3 text-muted-foreground"><span>{t("Frakt")}</span><span className="tabular-nums">{nok(order.shipping_price)}</span></div>}
              <div className="flex justify-between gap-3 p-3 font-semibold"><span>{t("Totalt")}</span><span className="tabular-nums" data-testid="shop-order-total">{nok(order.total_amount)}</span></div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 text-sm">
              <p className="flex items-start gap-2"><Mail className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span><span className="block font-medium">{order.name}</span>{order.email}</span></p>
              {order.phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-muted-foreground shrink-0" />{order.phone}</p>}
              {order.note && <p className="flex items-start gap-2 sm:col-span-2 rounded-lg bg-muted/40 p-3"><MessageSquare className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" /><span><span className="block text-xs text-muted-foreground">{t("Din melding")}</span>{order.note}</span></p>}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-3">
          <OrganizerContactBlock name={order.organizer_name} orgNumber={order.organizer_org_number} contact={order.organizer_contact} compact />
          <p className="text-xs text-muted-foreground" data-testid="shop-order-seller-note">{t("Selger er {org} (org.nr. {orgnr}). Betalingen går direkte til selgeren – Right in formidler bare bestillingen.", { org: order.organizer_name, orgnr: order.organizer_org_number || "–" })}</p>
          <Button asChild variant="ghost" size="sm" data-testid="shop-order-back-to-shop"><Link to={shopUrl(order.organizer_id)}><Store className="w-4 h-4 mr-1.5" /> {t("Butikken til {org}", { org: order.organizer_name })}</Link></Button>
        </div>
      </div>
    </PublicLayout>
  );
}
